/**
 * Demandes SAV déposées sur le site eurodecor-meubles.be.
 * Tables public.eds_sav_requests / eds_sav_photos : lecture et suivi réservés aux rôles admin et gérant (RLS).
 * Les photos sont dans le bucket PRIVÉ « sav-photos » : jamais d'URL publique, seulement des liens signés de 5 minutes
 * générés à l'ouverture du détail (aucune mise en cache).
 * La liste ne charge jamais le texte de la demande ; seul le détail le charge.
 */
import { supabase } from './supabaseClient';

export type SavRequestStatus = 'new' | 'in_progress' | 'closed' | 'spam';
export type SavRequestFilter = 'all' | 'new' | 'in_progress' | 'closed' | 'spam';

export const SAV_PHOTOS_BUCKET = 'sav-photos';
/** Durée de vie d'un lien signé de photo (secondes). */
export const SAV_PHOTO_LINK_SECONDS = 300;
export const SAV_REQUESTS_PAGE_SIZE = 50;

/** Colonnes affichées dans la liste (sans le texte de la demande). */
export interface SavRequestListItem {
  id: string;
  created_at: string;
  reference: string;
  name: string;
  store_name: string | null;
  status: SavRequestStatus;
  read_at: string | null;
  ack_sent_at: string | null;
  ack_error: string | null;
  /** Nombre de photos (jointure `eds_sav_photos(count)`) */
  eds_sav_photos: { count: number }[] | null;
}

export interface SavRequestDetail extends SavRequestListItem {
  email: string;
  phone: string | null;
  order_ref: string | null;
  purchase_date: string | null;
  message: string;
  consent_at: string;
  read_by: string | null;
  handled_at: string | null;
  handled_by: string | null;
  linked_sav_id: string | null;
  staff_notified: boolean;
  notify_error: string | null;
}

export interface SavPhoto {
  id: string;
  storage_path: string;
  mime_type: string;
  size_bytes: number;
}

export interface SavPhotoLink extends SavPhoto {
  /** Lien signé temporaire (null si la génération a échoué) */
  url: string | null;
}

export interface LinkedSav {
  id: string;
  sav_number: string;
  status: string;
}

const LIST_COLUMNS =
  'id, created_at, reference, name, store_name, status, read_at, ack_sent_at, ack_error, eds_sav_photos(count)';
const DETAIL_COLUMNS = `${LIST_COLUMNS}, email, phone, order_ref, purchase_date, message, consent_at, read_by, handled_at, handled_by, linked_sav_id, staff_notified, notify_error`;

/** Nombre de photos d'une demande de la liste. */
export const photoCount = (item: Pick<SavRequestListItem, 'eds_sav_photos'>): number =>
  item.eds_sav_photos?.[0]?.count ?? 0;

/** Identifiant auth.uid() de la session (≠ users.id de Luxori). */
async function getAuthUid(): Promise<string> {
  const { data, error } = await supabase.auth.getUser();
  if (error || !data.user) {
    throw new Error('Session expirée : reconnectez-vous.');
  }
  return data.user.id;
}

export const savRequestsService = {
  /** Nombre de nouvelles demandes (non lues, hors spam) : requête HEAD, aucune donnée personnelle ne transite. */
  async getUnreadCount(): Promise<number> {
    const { count, error } = await supabase
      .from('eds_sav_requests')
      .select('id', { count: 'exact', head: true })
      .is('read_at', null)
      .neq('status', 'spam');
    if (error) throw error;
    return count ?? 0;
  },

  async list(
    filter: SavRequestFilter = 'all',
    page = 0
  ): Promise<{ items: SavRequestListItem[]; hasMore: boolean }> {
    const from = page * SAV_REQUESTS_PAGE_SIZE;
    let query = supabase
      .from('eds_sav_requests')
      .select(LIST_COLUMNS)
      .order('created_at', { ascending: false })
      // une ligne de plus pour savoir s'il reste une page
      .range(from, from + SAV_REQUESTS_PAGE_SIZE);
    // « Toutes » exclut le spam (onglet dédié)
    query = filter === 'all' ? query.neq('status', 'spam') : query.eq('status', filter);
    const { data, error } = await query;
    if (error) throw error;
    const rows = (data ?? []) as unknown as SavRequestListItem[];
    return {
      items: rows.slice(0, SAV_REQUESTS_PAGE_SIZE),
      hasMore: rows.length > SAV_REQUESTS_PAGE_SIZE,
    };
  },

  async getById(id: string): Promise<SavRequestDetail | null> {
    const { data, error } = await supabase
      .from('eds_sav_requests')
      .select(DETAIL_COLUMNS)
      .eq('id', id)
      .maybeSingle();
    if (error) throw error;
    return (data as unknown as SavRequestDetail | null) ?? null;
  },

  /** Marque lue : read_by = auth.uid() (le trigger de garde refuse tout autre identifiant). */
  async markRead(id: string): Promise<void> {
    const uid = await getAuthUid();
    const { error } = await supabase
      .from('eds_sav_requests')
      .update({ read_at: new Date().toISOString(), read_by: uid })
      .eq('id', id);
    if (error) throw error;
  },

  async markUnread(id: string): Promise<void> {
    const { error } = await supabase
      .from('eds_sav_requests')
      .update({ read_at: null, read_by: null })
      .eq('id', id);
    if (error) throw error;
  },

  /** Change le statut ; « new » efface le traitement, les autres statuts l'attribuent à l'agent connecté. */
  async setStatus(id: string, status: SavRequestStatus): Promise<void> {
    const patch =
      status === 'new'
        ? { status, handled_at: null, handled_by: null }
        : { status, handled_at: new Date().toISOString(), handled_by: await getAuthUid() };
    const { error } = await supabase.from('eds_sav_requests').update(patch).eq('id', id);
    if (error) throw error;
  },

  /** Photos d'une demande, avec un lien signé de 5 minutes chacune (jamais d'URL publique). */
  async getPhotoLinks(requestId: string): Promise<SavPhotoLink[]> {
    const { data, error } = await supabase
      .from('eds_sav_photos')
      .select('id, storage_path, mime_type, size_bytes')
      .eq('request_id', requestId)
      .order('created_at', { ascending: true });
    if (error) throw error;
    const photos = (data ?? []) as SavPhoto[];
    if (photos.length === 0) return [];
    const { data: signed, error: signError } = await supabase.storage
      .from(SAV_PHOTOS_BUCKET)
      .createSignedUrls(
        photos.map(p => p.storage_path),
        SAV_PHOTO_LINK_SECONDS
      );
    if (signError) throw signError;
    const byPath = new Map((signed ?? []).map(s => [s.path, s.error ? null : s.signedUrl]));
    return photos.map(p => ({ ...p, url: byPath.get(p.storage_path) ?? null }));
  },

  /** Dossier SAV de Luxori lié à la demande (lien simple : peut avoir été supprimé depuis). */
  async getLinkedSav(savId: string): Promise<LinkedSav | null> {
    const { data, error } = await supabase
      .from('after_sales_services')
      .select('id, sav_number, status')
      .eq('id', savId)
      .maybeSingle();
    if (error) throw error;
    return (data as LinkedSav | null) ?? null;
  },

  /** Recherche un dossier SAV de Luxori par son numéro exact (ex. SAV-2026-001). */
  async findSavByNumber(savNumber: string): Promise<LinkedSav | null> {
    const { data, error } = await supabase
      .from('after_sales_services')
      .select('id, sav_number, status')
      .eq('sav_number', savNumber.trim().toUpperCase())
      .maybeSingle();
    if (error) throw error;
    return (data as LinkedSav | null) ?? null;
  },

  async setLinkedSav(id: string, savId: string | null): Promise<void> {
    const { error } = await supabase
      .from('eds_sav_requests')
      .update({ linked_sav_id: savId })
      .eq('id', id);
    if (error) throw error;
  },
};
